import DriveFileRenameOutlineOutlinedIcon from "@mui/icons-material/DriveFileRenameOutlineOutlined";
import type { TypographyProps } from "@mui/material";
import { IconButton, Stack, Tooltip, Typography } from "@mui/material";
import type { PropsWithChildren, ReactNode } from "react";
import { useMemo, useState } from "react";
import InlineTitleEditor from "@entrilia/shared/components/InlineTitleEditor";
import type { Validator } from "@entrilia/shared/utilities/validators";
import { combineValidators, minCharactersValidator, requiredValidator } from "@entrilia/shared/utilities/validators";

interface Props {
  title: string;
  TitleComponent?: ReactNode;
  tooltip: string;
  editingDisabled?: boolean;
  placeholder: string;
  validator?: Validator<string>;
  onTitleChanged?: (title: string) => void | Promise<void>;
  titleProps?: TypographyProps;
}

const defaultValidator = combineValidators(requiredValidator, minCharactersValidator(3, "Enter at least 3 letters"));

const EditableTitle = ({
  title,
  TitleComponent,
  tooltip,
  editingDisabled,
  onTitleChanged,
  placeholder,
  validator,
  titleProps = {},
  children,
}: PropsWithChildren<Props>) => {
  const [isRenaming, setIsRenaming] = useState(false);
  const [isApplying, setIsApplying] = useState(false);
  const [newTitle, setNewTitle] = useState(title ?? "");

  const validationError = useMemo(() => {
    const validationResult = (validator ?? defaultValidator)(newTitle);
    return validationResult.isValid ? undefined : validationResult.error;
  }, [newTitle, validator]);

  if (!isRenaming) {
    return (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
        {TitleComponent ?? (
          <Typography
            variant="h6"
            {...titleProps}
            sx={[{ lineHeight: "100%" }, ...(Array.isArray(titleProps.sx) ? titleProps.sx : [titleProps.sx])]}
          >
            {title}
          </Typography>
        )}
        {!editingDisabled && (
          <Tooltip title={tooltip} arrow disableInteractive>
            <IconButton
              onClick={() => {
                setIsRenaming(true);
              }}
              sx={{
                "@keyframes fadeInScale": {
                  "0%": { opacity: 0, transform: "scale(0.8)" },
                  "100%": { opacity: 1, transform: "scale(1)" },
                },
                animation: "fadeInScale 0.2s ease-in-out",
              }}
            >
              <DriveFileRenameOutlineOutlinedIcon />
            </IconButton>
          </Tooltip>
        )}
        {children}
      </Stack>
    );
  }

  return (
    <InlineTitleEditor
      title={title}
      onApply={async (appliedTitle) => {
        setIsApplying(true);
        try {
          await onTitleChanged?.(appliedTitle);
          setNewTitle(appliedTitle);
          setIsRenaming(false);
        } catch {
          // Keep the editor open so the user can retry after a failed async save.
        } finally {
          setIsApplying(false);
        }
      }}
      onClose={() => {
        setNewTitle(title);
        setIsRenaming(false);
      }}
      loading={isApplying}
      validationError={validationError}
      onTitleChanged={setNewTitle}
      placeholder={placeholder}
    />
  );
};

export default EditableTitle;
